import { useInfiniteQuery } from "@tanstack/react-query";
import { getLeadRatings } from "@/modules/lead-page/api/lead-page.v2.api";
import { GetLeadRatingsFiltersDto } from "@/modules/lead-page/dtos/index-v2";
import { useMemo, useCallback } from "react";
import { Rating } from "@/modules/lead-page/types";

interface UseLeadRatingsParams {
  filters?: GetLeadRatingsFiltersDto;
  enabled?: boolean;
}

interface LeadRatingsPage {
  data?: {
    page?: number;
    total?: number;
    pageSize?: number;
    results?: Rating[];
  };
}

export const useLeadRatings = ({
  filters,
  enabled = true,
}: UseLeadRatingsParams = {}) => {
  const pageSize = filters?.pageSize || 20;

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    ["leadRatings", filters],
    ({ pageParam = 1 }) =>
      getLeadRatings({ ...filters, page: pageParam, pageSize }),
    {
      getNextPageParam: (
        lastPage: LeadRatingsPage,
        allPages: LeadRatingsPage[],
      ) => {
        const totalFetched = allPages.length * pageSize;
        const total = lastPage?.data?.total || 0;
        return totalFetched < total ? allPages.length + 1 : undefined;
      },
      enabled: enabled && !!filters?.journeyUIds,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
  );

  const ratings = useMemo(
    () =>
      data?.pages?.flatMap(
        (page: LeadRatingsPage) => page?.data?.results || [],
      ) || [],
    [data?.pages],
  );
  const count = data?.pages?.[0]?.data?.total || 0;

  const fetchUntilReviewFound = useCallback(
    async (reviewId: string): Promise<boolean> => {
      if (ratings.some((r: Rating) => r.uid === reviewId)) return true;

      let canFetchMore = hasNextPage;

      while (canFetchMore) {
        const result = await fetchNextPage();
        const pages = result.data?.pages || [];
        const allRatings = pages.flatMap(
          (page: LeadRatingsPage) => page?.data?.results || [],
        );

        if (allRatings.some((r: Rating) => r.uid === reviewId)) return true;

        const lastPage = pages[pages.length - 1];
        canFetchMore = pages.length * pageSize < (lastPage?.data?.total || 0);
      }

      return false;
    },
    [ratings, hasNextPage, fetchNextPage, pageSize],
  );

  return {
    ratings,
    count,
    isLoading,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    fetchUntilReviewFound,
  };
};
